export const WALLET_ERROR_CODES = [
  // From the wallet-api URN catalog (1:1 with the server)
  'unauthorized',
  'token-expired',
  'signcount-replay',
  'challenge-expired',
  'challenge-not-found',
  'credential-not-found',
  'attestation-invalid',
  'assertion-invalid',
  'address-mismatch',
  'scope-not-allowed',
  'ttl-too-long',
  'credential-already-exists',
  'validation',
  'internal',
  'refresh-invalid',
  // S0 (account identity: email OTP, accounts/me, root envelopes)
  'rate-limited',
  'email-unverified',
  'email-conflict',
  'otp-invalid',
  'root-envelope-missing',
  // Client-only codes (see spec §7.1)
  // Root-envelope resolution (S0, client-side): three conditions the client
  // must keep apart, none of which has a server URN — the server only ever
  // says an envelope is missing (`root-envelope-missing`, above). Everything
  // after the fetch is a local diagnosis: the envelope names a wrap label this
  // build does not know, it is wrapped by something other than this
  // credential's PRF output, or it simply will not open. See
  // `@cef-ai/wallet-identity`'s `rootWrap.ts`, where each is a `WalletError`
  // subclass so callers can switch on `.code` AND keep `instanceof`.
  'root-wrap-version-unknown',
  'root-wrap-source-unsupported',
  'root-unwrap-failed',
  // A session response (register-finish / login-finish / session-refresh)
  // came back without a boolean `isOrigin` or without a non-empty string
  // `accountId`. Deliberately DISTINCT from `root-envelope-missing`, and
  // scoped to those two fields: absent `isOrigin` is falsy, so root resolution
  // silently takes the non-origin branch and fails as "this passkey cannot
  // open your wallet" — loud, but blaming the user's credential for what is
  // actually an older server or a field-stripping proxy; absent `accountId`
  // is read unconditionally downstream and propagates `undefined` as the
  // account's identity. Every other field keeps the unchecked-cast pattern;
  // these two earn the exception because neither has a safe default — one
  // redirects root derivation, the other names the account — rather than
  // merely losing data.
  'session-response-malformed',
  // Embedded registration (S0, client-side). Two conditions the popup
  // register flow must keep apart from each other and from `internal`, which
  // is what both used to collapse into. Client-only: the server never sees
  // either, because neither gets as far as a request.
  //
  //   `registration-unsupported` — `wallet:hello{intent:'register'}` reached a
  //   wallet surface with no registration UI. Only the cross-origin iframe
  //   can be that surface, and it is unreachable by construction (Safari
  //   blocks WebAuthn `create()` there, so `EmbedWallet.register()` always
  //   punches out to a popup), so this is a wiring fault — but a wiring fault
  //   that says which wiring, rather than "something went wrong on our side".
  //
  //   `registration-in-progress` — a SECOND register intent arrived while a
  //   prompt was still open. Reachable: a host may call `register()` again
  //   over a reused `PopupTransport`, and the reader has one popup with one
  //   half-finished flow in it. Distinct from `-unsupported` because the
  //   remedy is entirely different (finish or close the flow you have, versus
  //   nothing you can do), and neither remedy survives being described as
  //   `internal`.
  'registration-unsupported',
  'registration-in-progress',
  'prf-unsupported',
  // S1, client-side, emitted by the WALLET SURFACE and consumed by the SDK.
  // A WebAuthn ceremony run inside the wallet's cross-origin iframe was refused
  // by Permissions Policy — the frame was not delegated
  // `publickey-credentials-get`, so `navigator.credentials.get()` rejected
  // without ever painting a sheet.
  //
  // It exists because the browser reports that refusal and a reader dismissing
  // the passkey sheet as the SAME `NotAllowedError`, and the two need opposite
  // responses: this one must be retried on another surface, a dismissal must
  // not. Only the framed document can tell them apart (it alone can ask its own
  // Permissions Policy), and only a `code` survives the `wallet:error` envelope
  // — `err.name` does not — so the discrimination has to be carried as one.
  // Deliberately DISTINCT from `user-cancelled`, which the same handler throws
  // for the other branch, and from `internal`, which is what both collapsed
  // into before.
  'ceremony-policy-denied',
  // S1, client-side. The in-iframe ceremony was refused by Permissions Policy,
  // so the SDK must retry it in a popup — but `window.open` requires transient
  // user activation (unlike `navigator.credentials.get()`, which does not), and
  // this call has none left to spend. Deliberately DISTINCT from
  // `popup-blocked`: that reports a window we asked for and were denied, after
  // the fact; this reports that we declined to ask, because we could see the
  // ask would be swallowed. The remedy is a remedy — render a Continue button
  // and retry from its click — where `popup-blocked` from a gesture-less path
  // leaves the caller nothing to do.
  'gesture-required',
  'popup-blocked',
  'popup-closed',
  'user-cancelled',
  'origin-mismatch',
  'derivation-mismatch',
  // The iframe session is bearer-active (no signing keys); the caller must
  // step up (re-run a WebAuthn ceremony) before signing. Client-only — not a
  // wallet-api URN. Emitted by the SPA sign handler; matched by the embed-sdk.
  'needs-step-up',
] as const;

export type WalletErrorCode = (typeof WALLET_ERROR_CODES)[number];

export class WalletError extends Error {
  constructor(
    public readonly code: WalletErrorCode,
    public readonly detail?: string,
    public readonly traceId?: string,
    /**
     * HTTP status of the response this error was translated from, when there
     * was one. Set ONLY by `ApiClient.request()`; `undefined` for a network
     * error (no response arrived) and for every client-side `WalletError`.
     *
     * It exists because `code` alone cannot express "genuinely absent": a
     * peer that answers 404 without a problem+json body maps to `internal`,
     * which is indistinguishable from a 500 — and a caller that has to tell
     * "there is no such row" from "the service is broken" (see
     * `unwrapRootForCredential` in `@cef-ai/wallet-identity`) would otherwise
     * be left matching on the `HTTP 404` detail string.
     *
     * Deliberately NOT in `toJSON()`: that shape is the postMessage envelope
     * shared with the SDK, where the status of a request the other side never
     * made is noise. Status is for the process that made the call.
     */
    public readonly status?: number,
  ) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = 'WalletError';
  }

  toJSON() {
    return {
      name: this.name,
      code: this.code,
      detail: this.detail,
      traceId: this.traceId,
      message: this.message,
    };
  }
}
